import {
  AUTH_FLOW_ERROR_DEDUPE_WINDOW_MS,
  AUTH_FLOW_VIEWED_EVENT,
  buildAuthFlowErrorReport,
  detectAutomation,
  detectInAppBrowser,
  getAuthFlowFromPathname,
  isAuthFlowErrorDuplicate,
  type AuthFlow,
  type AuthFlowErrorType,
} from "@/lib/auth-flow-errors";

type PostHogProperties = Record<string, string | number | boolean | null | undefined>;

export type AuthFlowTrackerCapture = (
  event: string,
  properties?: PostHogProperties,
) => void;

export type AuthFlowErrorTrackerOptions = {
  pathname: string | null | undefined;
  sessionId: string;
  now?: () => number;
  userAgent?: string;
  webdriver?: boolean;
  viewportWidth?: number;
  getCaptchaContainerWidth?: () => number | null;
  captureEvent: AuthFlowTrackerCapture;
  captureException?: (error: unknown, properties?: PostHogProperties) => void;
  root?: ParentNode | null;
  consoleObject?: Pick<Console, "error" | "warn">;
  addErrorListener?: (handler: (event: Event) => void) => () => void;
};

const CLERK_ALERT_SELECTOR = [
  '[role="alert"]',
  ".cl-alert",
  ".cl-alertText",
  ".cl-formFieldErrorText",
  ".cl-formFieldError",
].join(",");

const CAPTCHA_WIDTH_SELECTORS = [
  ".cl-captcha",
  ".cf-turnstile",
  'iframe[src*="challenges.cloudflare.com"]',
];

type TrackedError = {
  errorType: AuthFlowErrorType | string;
  errorCode: string | null;
  message: string;
  at: number;
};

function stringifyConsoleArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === "string") {
        return arg;
      }
      if (arg instanceof Error) {
        return arg.message;
      }
      try {
        return String(arg);
      } catch {
        return "";
      }
    })
    .filter(Boolean)
    .join(" ");
}

export function collectClerkAlertMessages(root: ParentNode): string[] {
  const messages: string[] = [];
  const seen = new Set<string>();
  root.querySelectorAll(CLERK_ALERT_SELECTOR).forEach((element) => {
    const text = element.textContent?.replace(/\s+/g, " ").trim() ?? "";
    if (!text || seen.has(text)) {
      return;
    }
    seen.add(text);
    messages.push(text);
  });
  return messages;
}

export function measureCaptchaContainerWidth(
  root: ParentNode = document,
): number | null {
  for (const selector of CAPTCHA_WIDTH_SELECTORS) {
    const element = root.querySelector(selector);
    if (!(element instanceof Element)) {
      continue;
    }
    const width = element.getBoundingClientRect().width;
    if (width > 0) {
      return width;
    }
  }
  return null;
}

function defaultAddErrorListener(handler: (event: Event) => void): () => void {
  window.addEventListener("error", handler, true);
  return () => {
    window.removeEventListener("error", handler, true);
  };
}

export function createAuthFlowErrorTracker(options: AuthFlowErrorTrackerOptions): {
  start: () => void;
  stop: () => void;
} {
  const flow: AuthFlow | null = getAuthFlowFromPathname(options.pathname);
  const consoleObject = options.consoleObject ?? console;
  let started = false;
  let viewRecorded = false;
  let lastError: TrackedError | null = null;
  let restoreConsole: (() => void) | null = null;
  let removeErrorListener: (() => void) | null = null;
  let observer: MutationObserver | null = null;

  const getUserAgent = () =>
    options.userAgent ??
    (typeof navigator === "undefined" ? "" : navigator.userAgent);
  const getWebdriver = () =>
    options.webdriver ??
    (typeof navigator !== "undefined" && "webdriver" in navigator
      ? Boolean(navigator.webdriver)
      : false);
  const getViewportWidth = () =>
    options.viewportWidth ??
    (typeof window === "undefined" ? 0 : window.innerWidth);
  const getNow = () => (options.now ? options.now() : Date.now());
  const getRoot = () =>
    options.root ?? (typeof document === "undefined" ? null : document);

  const report = (message: string, failedScriptSrc?: string | null) => {
    if (!flow || !options.pathname) {
      return;
    }

    const reportPayload = buildAuthFlowErrorReport({
      flow,
      pathname: options.pathname,
      message,
      userAgent: getUserAgent(),
      webdriver: getWebdriver(),
      viewportWidth: getViewportWidth(),
      captchaContainerWidth: options.getCaptchaContainerWidth
        ? options.getCaptchaContainerWidth()
        : measureCaptchaContainerWidth(getRoot() ?? document),
      sessionId: options.sessionId,
      failedScriptSrc,
    });
    if (!reportPayload) {
      return;
    }

    const nextError: TrackedError = {
      errorType: reportPayload.properties.error_type,
      errorCode: reportPayload.properties.error_code,
      message: reportPayload.properties.message,
      at: getNow(),
    };
    if (
      lastError &&
      isAuthFlowErrorDuplicate(
        lastError,
        nextError,
        AUTH_FLOW_ERROR_DEDUPE_WINDOW_MS,
      )
    ) {
      return;
    }
    lastError = nextError;

    options.captureEvent(reportPayload.event, reportPayload.properties);

    if (
      options.captureException &&
      reportPayload.properties.error_type !== "clerk_validation"
    ) {
      options.captureException(new Error(reportPayload.properties.message), {
        ...reportPayload.properties,
      });
    }
  };

  const scanAlerts = () => {
    const root = getRoot();
    if (!root) {
      return;
    }
    for (const message of collectClerkAlertMessages(root)) {
      report(message);
    }
  };

  const onConsole = (...args: unknown[]) => {
    const message = stringifyConsoleArgs(args);
    if (message) {
      report(message);
    }
  };

  const onScriptError = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLScriptElement) || !target.src) {
      return;
    }
    const message =
      "message" in event && typeof event.message === "string" && event.message
        ? event.message
        : "Script error";
    report(message, target.src);
  };

  const recordView = () => {
    if (!flow || !options.pathname || viewRecorded) {
      return;
    }
    viewRecorded = true;
    const userAgent = getUserAgent();
    const inAppBrowser = detectInAppBrowser(userAgent);
    const automation = detectAutomation(getWebdriver(), userAgent);
    const captchaContainerWidth = options.getCaptchaContainerWidth
      ? options.getCaptchaContainerWidth()
      : measureCaptchaContainerWidth(getRoot() ?? document);
    const properties: PostHogProperties = {
      flow,
      pathname: options.pathname,
      session_id: options.sessionId,
      user_agent: userAgent,
      viewport_width: getViewportWidth(),
      captcha_container_width: captchaContainerWidth,
      in_app_browser: inAppBrowser,
      automation,
    };
    options.captureEvent(AUTH_FLOW_VIEWED_EVENT, properties);
  };

  const start = () => {
    if (!flow || started) {
      return;
    }
    started = true;
    recordView();

    const originalError = consoleObject.error.bind(consoleObject);
    const originalWarn = consoleObject.warn.bind(consoleObject);
    consoleObject.error = ((...args: Parameters<Console["error"]>) => {
      originalError(...args);
      onConsole(...args);
    }) as Console["error"];
    consoleObject.warn = ((...args: Parameters<Console["warn"]>) => {
      originalWarn(...args);
      onConsole(...args);
    }) as Console["warn"];
    restoreConsole = () => {
      consoleObject.error = originalError;
      consoleObject.warn = originalWarn;
    };

    const addErrorListener = options.addErrorListener ?? defaultAddErrorListener;
    removeErrorListener = addErrorListener(onScriptError);

    const root = getRoot();
    if (root && typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(() => {
        scanAlerts();
      });
      observer.observe(root, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    }
    scanAlerts();
  };

  const stop = () => {
    started = false;
    restoreConsole?.();
    restoreConsole = null;
    removeErrorListener?.();
    removeErrorListener = null;
    observer?.disconnect();
    observer = null;
  };

  return { start, stop };
}
